import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  MessstellenListeSummary,
  kennzeichnungLabel,
  pruefergebnisCss,
  pruefergebnisText,
  signalStatusCss,
  signalStatusText
} from '../../../../../Models/messstelle.model';
import { InbetriebnahmeService } from '../../../../../Services/inbetriebnahme.service';
import {
  IbnMessstellenExportModus,
  IbnProtokollMessstellenAuswahl,
  IbnProtokollMessstellenDialogData,
  MessstelleAuswahlOption,
  MessstellenFilterOptionen,
  auswahlAnzeigeName,
  leereFilterOptionen,
  messstellenAuswahlOptionen,
  messstellenFilterOptionen
} from '../ibn-protokoll-auswahl.model';

/**
 * Auswahl mehrerer Messstellen für den Export.
 *
 * EINE Oberfläche für beide Mehrfach-Varianten: Sammel-PDF und ZIP
 * unterscheiden sich nur im Ergebnis, nicht in der Auswahl. Der Modus steuert
 * deshalb ausschließlich Beschriftungen – Filter, Trefferliste und
 * Exportliste sind identisch.
 *
 * Ablauf:
 *   Inbetriebnahme wählen → filtern → einzeln oder alle Angezeigten in die
 *   Exportliste übernehmen → exportieren.
 *
 * Leerer Filter + „Alle angezeigten hinzufügen“ bedeutet damit automatisch
 * „alle Messstellen der Inbetriebnahme“; eine eigene Alle-/Manche-Option
 * braucht es nicht.
 */
@Component({
  selector: 'app-ibn-protokoll-messstellen-dialog',
  templateUrl: './ibn-protokoll-messstellen-dialog.component.html',
  styleUrls: ['./ibn-protokoll-messstellen-dialog.component.css']
})
export class IbnProtokollMessstellenDialogComponent implements OnInit {

  readonly modus: IbnMessstellenExportModus;

  inbetriebnahmen: MessstellenListeSummary[] = [];
  inbetriebnahmeId: number | null = null;

  /** Alle Messstellen der gewählten Inbetriebnahme. */
  alle: MessstelleAuswahlOption[] = [];

  /** Was der aktuelle Filter davon übrig lässt. */
  treffer: MessstelleAuswahlOption[] = [];

  /** Die endgültige Auswahl, in der Reihenfolge des Hinzufügens. */
  exportliste: MessstelleAuswahlOption[] = [];

  // ── Filter (aus der Messstellenliste übernommen) ──
  suche = '';
  filterAnlagenteil = '';
  filterKategorie = '';
  filterSignalStatus = '';
  filterPruefergebnis = '';

  /**
   * Die auswählbaren Filterwerte – ausschließlich aus den Messstellen der
   * aktuell gewählten Inbetriebnahme abgeleitet.
   */
  filterOptionen: MessstellenFilterOptionen = leereFilterOptionen();

  /** Beschriftung der Kennzeichnungsspalte dieser Liste – „KKS“, „AKZ“, … */
  kennzeichnungTitel = 'Kennzeichnung';

  ladeMessstellen = false;
  ladeFehler = '';

  /** Schnelle Zugehörigkeitsprüfung für die Trefferliste. */
  private gewaehlteIds = new Set<number>();

  constructor(
    public dialogRef: MatDialogRef<
      IbnProtokollMessstellenDialogComponent,
      IbnProtokollMessstellenAuswahl | null
    >,
    @Inject(MAT_DIALOG_DATA) public data: IbnProtokollMessstellenDialogData,
    private service: InbetriebnahmeService
  ) {
    this.modus = data.modus;
  }

  ngOnInit(): void {
    this.inbetriebnahmen = this.data.projekt?.inbetriebnahmen ?? [];

    if (this.inbetriebnahmen.length === 1) {
      this.inbetriebnahmeId = this.inbetriebnahmen[0].id;
      this.ladeMessstellenListe(this.inbetriebnahmeId);
    }
  }

  // ── Beschriftungen je Modus ──

  get titel(): string {
    return this.modus === 'zip'
      ? 'Mehrere Messstellen als einzelne PDFs im ZIP'
      : 'Mehrere Messstellen in einem PDF';
  }

  get beschreibung(): string {
    return this.modus === 'zip'
      ? 'Für jede gewählte Messstelle entsteht ein eigenes vollständiges PDF; alle zusammen kommen in eine ZIP-Datei.'
      : 'Projekt- und IBN-Daten stehen einmal am Anfang, danach folgt jede gewählte Messstelle auf einer eigenen Seite.';
  }

  get exportButtonText(): string {
    return this.modus === 'zip' ? 'ZIP erstellen' : 'PDF erstellen';
  }

  get exportButtonIcon(): string {
    return this.modus === 'zip' ? 'folder_zip' : 'picture_as_pdf';
  }

  // ── Laden ──

  onInbetriebnahmeChange(id: number): void {
    this.inbetriebnahmeId = id;

    // Die Auswahl gilt immer innerhalb EINER Inbetriebnahme – ein Wechsel
    // macht die bisherige Exportliste damit gegenstandslos.
    this.leeren();

    this.alle = [];
    this.treffer = [];
    this.filterOptionen = leereFilterOptionen();

    this.ladeMessstellenListe(id);
  }

  private ladeMessstellenListe(listeId: number): void {
    this.ladeMessstellen = true;
    this.ladeFehler = '';

    this.service.getListe(listeId).subscribe({
      next: liste => {
        this.alle = messstellenAuswahlOptionen(liste);

        // Die Anlagenteile der Liste geben nur die Reihenfolge vor. Welche
        // Werte im Filter stehen, entscheiden allein die Messstellen dieser
        // Inbetriebnahme.
        this.filterOptionen = messstellenFilterOptionen(
          this.alle,
          liste.anlagenteile
        );

        this.kennzeichnungTitel = kennzeichnungLabel(liste.kennzeichnungsArt);

        this.bereinigeFilterAuswahl();
        this.aktualisiereTreffer();

        this.ladeMessstellen = false;
      },
      error: () => {
        this.alle = [];
        this.treffer = [];
        this.filterOptionen = leereFilterOptionen();
        this.ladeMessstellen = false;
        this.ladeFehler = 'Die Messstellen dieser Inbetriebnahme konnten nicht geladen werden.';
      }
    });
  }

  // ── Filter ──

  aktualisiereTreffer(): void {
    const suchbegriff = this.suche.trim().toLowerCase();

    this.treffer = this.alle.filter(m => {

      // Suche über Kennzeichnung und Langtext (und was sonst im Suchtext steht).
      if (suchbegriff && !m.suchtext.includes(suchbegriff)) {
        return false;
      }

      if (this.filterAnlagenteil) {
        const passt = this.filterAnlagenteil === '__keiner__'
          ? m.anlagenteilId == null
          : m.anlagenteilId === Number(this.filterAnlagenteil);

        if (!passt) {
          return false;
        }
      }

      if (this.filterKategorie) {
        const passt = this.filterKategorie === '__keine__'
          ? m.kategorieName.length === 0
          : m.kategorieName === this.filterKategorie;

        if (!passt) {
          return false;
        }
      }

      if (this.filterSignalStatus && m.signalStatus !== Number(this.filterSignalStatus)) {
        return false;
      }

      if (this.filterPruefergebnis && m.pruefergebnis !== Number(this.filterPruefergebnis)) {
        return false;
      }

      return true;
    });
  }

  setzeFilterZurueck(): void {
    this.suche = '';
    this.filterAnlagenteil = '';
    this.filterKategorie = '';
    this.filterSignalStatus = '';
    this.filterPruefergebnis = '';

    this.aktualisiereTreffer();
  }

  /**
   * Hält die gesetzten Filter gültig.
   *
   * Nach einem Wechsel der Inbetriebnahme kann ein zuvor gewählter Wert dort
   * nicht mehr vorkommen. Ein solcher Filter faellt auf "Alle" zurueck – sonst
   * zeigte die Trefferliste ohne erkennbaren Grund nichts an. Filter, deren
   * Wert es weiterhin gibt, bleiben erhalten.
   *
   * Die Exportliste wird hier bewusst nicht angefasst: sie ist die Auswahl des
   * Benutzers und keine Folge des Filters.
   */
  private bereinigeFilterAuswahl(): void {
    const optionen = this.filterOptionen;

    if (this.filterAnlagenteil === '__keiner__') {
      if (!optionen.hatOhneAnlagenteil) {
        this.filterAnlagenteil = '';
      }
    } else if (
      this.filterAnlagenteil
      && !optionen.anlagenteile.some(a => String(a.id) === this.filterAnlagenteil)
    ) {
      this.filterAnlagenteil = '';
    }

    if (this.filterKategorie === '__keine__') {
      if (!optionen.hatOhneKategorie) {
        this.filterKategorie = '';
      }
    } else if (
      this.filterKategorie
      && !optionen.kategorien.includes(this.filterKategorie)
    ) {
      this.filterKategorie = '';
    }

    if (
      this.filterSignalStatus
      && !optionen.signalStatus.some(o => String(o.wert) === this.filterSignalStatus)
    ) {
      this.filterSignalStatus = '';
    }

    if (
      this.filterPruefergebnis
      && !optionen.pruefergebnis.some(o => String(o.wert) === this.filterPruefergebnis)
    ) {
      this.filterPruefergebnis = '';
    }
  }

  /**
   * Ein Filter mit nur einem einzigen vorkommenden Wert kann nichts
   * eingrenzen. Er wird deshalb gar nicht erst angeboten.
   */
  get zeigeSignalStatusFilter(): boolean {
    return this.filterOptionen.signalStatus.length > 1;
  }

  get zeigePruefergebnisFilter(): boolean {
    return this.filterOptionen.pruefergebnis.length > 1;
  }

  /** Ohne benannte Anlagenteile bliebe nur „Alle“ – dann entfällt der Filter. */
  get zeigeAnlagenteilFilter(): boolean {
    return this.filterOptionen.anlagenteile.length > 0;
  }

  get zeigeKategorieFilter(): boolean {
    return this.filterOptionen.kategorien.length > 0;
  }

  get hatFilter(): boolean {
    return this.suche.trim().length > 0
      || this.filterAnlagenteil !== ''
      || this.filterKategorie !== ''
      || this.filterSignalStatus !== ''
      || this.filterPruefergebnis !== '';
  }

  /** Wie viele der aktuellen Treffer noch nicht in der Exportliste stehen. */
  get offeneTreffer(): number {
    return this.treffer.filter(m => !this.gewaehlteIds.has(m.id)).length;
  }

  // ── Exportliste ──

  istGewaehlt(messstelle: MessstelleAuswahlOption): boolean {
    return this.gewaehlteIds.has(messstelle.id);
  }

  hinzufuegen(messstelle: MessstelleAuswahlOption): void {
    if (this.gewaehlteIds.has(messstelle.id)) {
      return;
    }

    this.gewaehlteIds.add(messstelle.id);
    this.exportliste = [...this.exportliste, messstelle];
  }

  /**
   * Übernimmt alle aktuell angezeigten Treffer.
   * Ohne Filter sind das alle Messstellen der Inbetriebnahme.
   */
  alleAngezeigtenHinzufuegen(): void {
    const neue = this.treffer.filter(m => !this.gewaehlteIds.has(m.id));

    for (const messstelle of neue) {
      this.gewaehlteIds.add(messstelle.id);
    }

    this.exportliste = [...this.exportliste, ...neue];
  }

  entfernen(messstelle: MessstelleAuswahlOption): void {
    this.gewaehlteIds.delete(messstelle.id);
    this.exportliste = this.exportliste.filter(m => m.id !== messstelle.id);
  }

  leeren(): void {
    this.gewaehlteIds.clear();
    this.exportliste = [];
  }

  // ── Anzeige ──

  anzeigeName(messstelle: MessstelleAuswahlOption): string {
    return auswahlAnzeigeName(messstelle);
  }

  statusText(messstelle: MessstelleAuswahlOption): string {
    return signalStatusText(messstelle.signalStatus);
  }

  statusCss(messstelle: MessstelleAuswahlOption): string {
    return signalStatusCss(messstelle.signalStatus);
  }

  ergebnisText(messstelle: MessstelleAuswahlOption): string {
    return pruefergebnisText(messstelle.pruefergebnis);
  }

  ergebnisCss(messstelle: MessstelleAuswahlOption): string {
    return pruefergebnisCss(messstelle.pruefergebnis);
  }

  spurId(_index: number, messstelle: MessstelleAuswahlOption): number {
    return messstelle.id;
  }

  // ── Abschluss ──

  get istGueltig(): boolean {
    return this.inbetriebnahmeId != null && this.exportliste.length > 0;
  }

  abbrechen(): void {
    this.dialogRef.close(null);
  }

  exportieren(): void {
    if (!this.istGueltig || this.inbetriebnahmeId == null) {
      return;
    }

    const inbetriebnahme = this.inbetriebnahmen.find(i => i.id === this.inbetriebnahmeId);

    this.dialogRef.close({
      modus: this.modus,
      projektId: this.data.projekt.id,
      inbetriebnahmeId: this.inbetriebnahmeId,
      inbetriebnahmeName: inbetriebnahme?.name ?? 'Inbetriebnahme',
      messstellen: this.exportliste
    });
  }
}
